import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Paper,
  Button,
} from "@mui/material";
import { FIELD_TYPES } from "../../../lib/field-types";
import { Delete, MousePointerIcon, Save } from "lucide-react";
import { ContentCopy } from "@mui/icons-material";

export default function PropertiesPanel({
  selectedField,
  onUpdateField,
  onDeleteField,
  onDeselectField,
}) {
  const [localField, setLocalField] = useState(null);

  useEffect(() => {
    setLocalField(selectedField);
  }, [selectedField]);

  if (!selectedField || !localField) {
    return (
      <Paper
        sx={{
          width: 320,
          bgcolor: "#fff",
          borderLeft: "1px solid #e5e7eb",
          p: 3,
        }}
        data-testid="properties-panel"
      >
        <Typography variant="h6" gutterBottom>
          Field Properties
        </Typography>
        <Box sx={{ textAlign: "center", py: 6 }} data-testid="no-field-selected">
          <MousePointerIcon sx={{ fontSize: 48, color: "gray", mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            No field selected
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click on a field to edit its properties
          </Typography>
        </Box>
      </Paper>
    );
  }

  const updateLocalField = (updates) => {
    const updated = { ...localField, ...updates };
    setLocalField(updated);
    onUpdateField(localField.id, updates);
  };

  const fieldTypeInfo = FIELD_TYPES[localField.type];
  const showOptions = ["select", "radio", "checkbox"].includes(localField.type);

  const handleOptionsChange = (value) => {
    const options = value.split("\n").filter((option) => option.trim());
    updateLocalField({ options });
  };

  return (
    <Paper
      sx={{
        width: 320,
        bgcolor: "#fff",
        borderLeft: "1px solid #e5e7eb",
        p: 3,
        overflowY: "auto",
      }}
      data-testid="properties-panel"
    >
      <Typography variant="h6" gutterBottom>
        Field Properties
      </Typography>

      {/* Field Type */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="field-type-label">Field Type</InputLabel>
        <Select
          labelId="field-type-label"
          value={localField.type}
          onChange={(e) => updateLocalField({ type: e.target.value })}
          data-testid="select-field-type"
        >
          {Object.values(FIELD_TYPES).map((ft) => (
            <MenuItem key={ft.type} value={ft.type}>
              {ft.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Field Label */}
      <TextField
        label="Label"
        fullWidth
        value={localField.label}
        onChange={(e) => updateLocalField({ label: e.target.value })}
        sx={{ mb: 2 }}
        data-testid="input-field-label"
      />

      {/* Placeholder */}
      <TextField
        label="Placeholder"
        fullWidth
        value={localField.placeholder || ""}
        onChange={(e) => updateLocalField({ placeholder: e.target.value })}
        sx={{ mb: 2 }}
        data-testid="input-field-placeholder"
      />

      {/* Width */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="field-width-label">Width</InputLabel>
        <Select
          labelId="field-width-label"
          value={localField.width}
          onChange={(e) => updateLocalField({ width: e.target.value })}
          data-testid="select-field-width"
        >
          <MenuItem value="full">Full Width</MenuItem>
          <MenuItem value="half">Half Width</MenuItem>
          <MenuItem value="quarter">Quarter Width</MenuItem>
        </Select>
      </FormControl>

      {/* Options */}
      {showOptions && (
        <TextField
          label="Options"
          fullWidth
          multiline
          rows={4}
          placeholder="Enter each option on a new line"
          value={localField.options?.join("\n") || ""}
          onChange={(e) => handleOptionsChange(e.target.value)}
          sx={{ mb: 2 }}
          data-testid="textarea-field-options"
        />
      )}

      {/* Validation */}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Validation
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={localField.required}
            onChange={(e) => updateLocalField({ required: e.target.checked })}
            data-testid="checkbox-field-required"
          />
        }
        label="Required field"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={localField.unique}
            onChange={(e) => updateLocalField({ unique: e.target.checked })}
            data-testid="checkbox-field-unique"
          />
        }
        label="Unique values only"
      />

      {/* MongoDB Mapping */}
      <Box sx={{ p: 2, bgcolor: "#f9fafb", borderRadius: 1, my: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          MongoDB Mapping
        </Typography>
        <Typography variant="caption" display="flex" justifyContent="space-between">
          <span>Field ID:</span> <span>{localField.id}</span>
        </Typography>
        <Typography variant="caption" display="flex" justifyContent="space-between">
          <span>Collection:</span> <span>form_responses</span>
        </Typography>
        <Typography variant="caption" display="flex" justifyContent="space-between">
          <span>Type:</span> <span>{fieldTypeInfo?.mongoType || "String"}</span>
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Button
        fullWidth
        variant="contained"
        startIcon={<Save />}
        sx={{ mb: 1 }}
        onClick={() => {}}
        data-testid="button-update-field"
      >
        Update Field
      </Button>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<ContentCopy />}
        sx={{ mb: 1 }}
        onClick={() => console.log("Duplicate field:", localField)}
        data-testid="button-duplicate-field"
      >
        Duplicate Field
      </Button>
      <Button
        fullWidth
        variant="contained"
        color="error"
        startIcon={<Delete />}
        onClick={() => {
          onDeleteField(localField.id);
          onDeselectField();
        }}
        data-testid="button-delete-field"
      >
        Delete Field
      </Button>
    </Paper>
  );
}
